import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import { useTenantContext } from '../../context/TenantContext';
import { CreditCard, Loader2, Lock, User, Mail, Phone, Home, MapPin, LogOut, UserCheck, UserPlus, CheckCircle } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { PublicLayout } from '../../components/layout/PublicLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { createOrUpdateCustomerProfile, getCustomerProfileByToken } from '../../services/customerProfileService';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../../lib/supabase';
import { PaymentModal } from '../../components/shop/PaymentModal';
import { initiateOrderPayment } from '../../services/shop/orderPaymentService';
import { getDomainFilterParams } from '../../hooks/useDomainQuery';

// Mapping d'icônes pour personnalisation - Utilise toutes les icônes Lucide
const iconMap = LucideIcons as Record<string, React.ElementType>;

function hexToRgba(hex: string, alpha = 1) {
  const cleaned = hex.replace('#', '');
  const bigint = parseInt(cleaned.length === 3
    ? cleaned.split('').map((c) => c + c).join('')
    : cleaned, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

interface CartItem {
  productId: number;
  title: string;
  price: number;
  currency: string;
  quantity: number;
  imageUrl?: string;
  slug: string;
}

interface CheckoutFormData {
  fullName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  country: string;
  postalCode: string;
  notes: string;
}

const fadeIn = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } } };

const CheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, signOut } = useAuth();
  const { tenant, previewConfig } = useTenantContext();

  // Thème Boutique (preview > tenant)
  const shopTheme = React.useMemo(() => {
    const base = (previewConfig?.theme?.notFound) ?? (tenant?.config?.theme?.notFound) ?? ({} as any);
    const themeColors = {
      c1: (tenant?.config?.color1 as string) || '#fe8d17', // primaire (bouton)
      c2: (tenant?.config?.color2 as string) || '#008aef', // secondaire (bouton)
      c3: (tenant?.config?.color3 as string) || '#ffffff',
      c4: (tenant?.config?.color4 as string) || '#9ca3af',
    };
    const defaults = {
      overlay: {
        enabled: true,
        useGradient: true,
        orientation: 'to-b', // to-b, to-t, to-r, to-l, to-tr, to-br, to-tl, to-bl
        color1: '#08062b',
        color2: '#08062b',
        color3: '#08062b',
        opacity1: 0.95,
        opacity2: 0.80,
        opacity3: 0.95,
      },
      icons: [
        { enabled: true, icon: 'CreditCard', color: themeColors.c1, size: 32 }, // top-left
        { enabled: true, icon: 'Sparkles',   color: themeColors.c2, size: 28 }, // top-right
        { enabled: true, icon: 'Diamond',    color: themeColors.c2, size: 24 }, // bottom-left
        { enabled: true, icon: 'Star',       color: themeColors.c1, size: 30 }, // bottom-right
      ]
    } as any;
    return {
      overlay: {
        enabled: base.overlay?.enabled ?? defaults.overlay.enabled,
        useGradient: base.overlay?.useGradient ?? defaults.overlay.useGradient,
        orientation: base.overlay?.orientation ?? defaults.overlay.orientation,
        color1: base.overlay?.color1 ?? defaults.overlay.color1,
        color2: base.overlay?.color2 ?? defaults.overlay.color2,
        color3: base.overlay?.color3 ?? defaults.overlay.color3,
        opacity1: base.overlay?.opacity1 ?? defaults.overlay.opacity1,
        opacity2: base.overlay?.opacity2 ?? defaults.overlay.opacity2,
        opacity3: base.overlay?.opacity3 ?? defaults.overlay.opacity3,
      },
      icons: (base.icons && Array.isArray(base.icons) && base.icons.length === 4)
        ? base.icons.map((i: any, idx: number) => ({
            enabled: i?.enabled ?? defaults.icons[idx].enabled,
            icon: i?.icon ?? defaults.icons[idx].icon,
            color: i?.color ?? defaults.icons[idx].color,
            size: Number(i?.size ?? defaults.icons[idx].size),
          }))
        : defaults.icons,
    };
  }, [tenant, previewConfig]);
  
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [currentOrder, setCurrentOrder] = useState<any>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUserProfile, setCurrentUserProfile] = useState<any>(null);
  
  const [formData, setFormData] = useState<CheckoutFormData>({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    country: '',
    postalCode: '',
    notes: ''
  });

  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    // Charger le panier depuis localStorage
    const loadCart = async () => {
      if (authChecked) return; // Éviter les appels multiples
      
      setIsLoading(true);
      try {
        const cartString = localStorage.getItem('shop_cart');
        const cart = cartString ? JSON.parse(cartString) : [];
        
        if (cart.length === 0) {
          // Rediriger vers le panier si celui-ci est vide
          toast({
            title: 'Panier vide',
            description: 'Votre panier de produits est vide',
            variant: 'default'
          });
          navigate('/boutique/panier');
          return;
        }
        
        setCartItems(cart);
        
        // Vérifier l'authentification shop (customer_access_token) en priorité
        const customerToken = localStorage.getItem('customer_access_token');
        if (customerToken) {
          try {
            const profile = await getCustomerProfileByToken(customerToken);
            
            if (profile) {
              setIsAuthenticated(true);
              setCurrentUserProfile({
                type: 'shop_customer',
                email: profile.email,
                fullName: profile.full_name || profile.email.split('@')[0]
              });
              
              setFormData(prev => ({
                ...prev,
                fullName: profile.full_name || '',
                email: profile.email || '',
                phone: profile.phone || '',
                address: profile.address || '',
                city: profile.city || '',
                country: profile.country || '',
                postalCode: profile.postal_code || ''
              }));
            } else {
              // Token invalide, le supprimer
              localStorage.removeItem('customer_access_token');
              setIsAuthenticated(false);
              setCurrentUserProfile(null);
            }
          } catch (error) {
            console.error('Erreur lors de la vérification du token client:', error);
            localStorage.removeItem('customer_access_token');
            setIsAuthenticated(false);
            setCurrentUserProfile(null);
          }
        } else if (user) {
          // Fallback sur l'authentification Supabase
          setIsAuthenticated(true);
          setCurrentUserProfile({
            type: 'supabase',
            email: user.email,
            fullName: user.user_metadata?.full_name || ''
          });
          
          setFormData(prev => ({
            ...prev,
            fullName: user.user_metadata?.full_name || '',
            email: user.email || '',
            phone: user.user_metadata?.phone || ''
          }));
        } else {
          setIsAuthenticated(false);
          setCurrentUserProfile(null);
        }
        
        setAuthChecked(true);
      } catch (error) {
        console.error('Erreur lors du chargement du panier:', error);
        toast({
          title: 'Erreur',
          description: 'Impossible de charger votre panier',
          variant: 'destructive'
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadCart();
  }, [user, navigate, toast, authChecked]);

  // Fonction de déconnexion
  const handleLogout = async () => {
    try {
      await signOut();
      setIsAuthenticated(false);
      setCurrentUserProfile(null);
      
      // Réinitialiser les champs du formulaire
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        country: '',
        postalCode: '',
        notes: ''
      });
      
      toast({
        title: 'Déconnexion réussie',
        description: 'Vous avez été déconnecté avec succès',
        variant: 'default'
      });
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
      toast({
        title: 'Erreur',
        description: 'Erreur lors de la déconnexion',
        variant: 'destructive'
      });
    }
  };

  // Calculer le total du panier
  const cartTotal = cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  const formattedTotal = new Intl.NumberFormat('fr-FR').format(cartTotal);
  const currency = 'XAF'; // XAF est le code ISO pour le franc CFA

  // Gestionnaire de changement de champ du formulaire
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Gestionnaire de soumission du formulaire
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Valider les champs requis
      if (!formData.fullName || !formData.email || !formData.phone || !formData.address || !formData.city) {
        toast({
          title: 'Champs requis',
          description: 'Veuillez remplir tous les champs obligatoires',
          variant: 'destructive'
        });
        setIsSubmitting(false);
        return;
      }
      
      // Créer ou mettre à jour le profil client
      let customerProfileId;
      
      // Vérifier si un profil existe déjà avec cet email
      const { data: existingProfiles } = await supabase
        .from('shop_customer_profiles')
        .select('id')
        .eq('email', formData.email)
        .limit(1);
      
      if (existingProfiles && existingProfiles.length > 0) {
        // Mettre à jour le profil existant
        customerProfileId = existingProfiles[0].id;
        await supabase
          .from('shop_customer_profiles')
          .update({
            full_name: formData.fullName,
            phone: formData.phone,
            address: formData.address,
            city: formData.city,
            country: formData.country,
            postal_code: formData.postalCode,
            updated_at: new Date().toISOString()
          })
          .eq('id', customerProfileId);
      } else {
        // Créer un nouveau profil
        const { data: newProfile, error } = await supabase
          .from('shop_customer_profiles')
          .insert({
            email: formData.email,
            full_name: formData.fullName,
            phone: formData.phone,
            address: formData.address,
            city: formData.city,
            country: formData.country,
            postal_code: formData.postalCode,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .select()
          .single();
        
        if (error) throw error;
        customerProfileId = newProfile.id;
      }
      
      // Récupérer les paramètres de domaine
      const { domain, tenantId } = getDomainFilterParams();
      
      // Générer un code de commande unique
      const orderCode = `ORD-${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}-${new Date().getFullYear().toString().substring(2)}`;
      
      // Créer la commande
      const { data: order, error: orderError } = await supabase
        .from('shop_orders')
        .insert({
          order_number: orderCode,
          created_at: new Date().toISOString(),
          customer_name: formData.fullName,
          customer_email: formData.email,
          shipping_address: {
            address: formData.address,
            city: formData.city,
            country: formData.country,
            postal_code: formData.postalCode
          },
          billing_address: {
            address: formData.address,
            city: formData.city,
            country: formData.country,
            postal_code: formData.postalCode
          },
          total_amount: cartItems.reduce((total, item) => total + (item.price * item.quantity), 0),
          shipping_cost: 0, // À calculer selon les besoins
          status: 'pending_payment',
          notes: formData.notes,
          customer_profile_id: customerProfileId,
          domain: domain || undefined,
          tenant_id: parseInt(tenantId)
        })
        .select()
        .single();
      
      if (orderError) throw orderError;
      
      // Créer les éléments de la commande
      const orderItems = cartItems.map(item => ({
        order_id: order.id,
        product_id: item.productId,
        quantity: item.quantity,
        price_at_purchase: item.price
      }));
      
      const { error: itemsError } = await supabase
        .from('shop_order_items')
        .insert(orderItems);
      
      if (itemsError) throw itemsError;
      
      // Afficher le modal de paiement
      setCurrentOrder({
        id: order.id,
        order_code: order.order_number,
        total_amount: order.total_amount,
        customer_name: order.customer_name,
        customer_email: order.customer_email
      });
      setShowPaymentModal(true);
      
    } catch (error) {
      console.error('Erreur lors de la création de la commande:', error);
      toast({
        title: 'Erreur',
        description: 'Une erreur est survenue lors de la création de votre commande de produits',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }

    // Le code de redirection vers la page de confirmation est maintenant géré après le paiement réussi
  };

  if (isLoading) {
    return (
      <PublicLayout>
        <div className="min-h-screen text-white" style={{ backgroundColor: tenant?.config?.color2 || '#08062b' }}>
          <div className="flex flex-col justify-center items-center h-screen">
            <Loader2 className="animate-spin h-12 w-12" style={{ color: tenant?.config?.color1 || '#fe8d17' }} />
            <span className="mt-6 text-xl" style={{ color: tenant?.config?.color4 || '#9ca3af' }}>Chargement de la commande...</span>
          </div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <SEOMetaTags
        title="Finaliser la Commande - SIS"
        description="Entrez vos informations pour finaliser votre achat de produits en toute sécurité."
      />

      <div className="text-white relative overflow-hidden min-h-screen">
        {/* Fond avec la nouvelle palette de couleurs SIS */}
        <div 
          className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
          style={{
            backgroundImage: tenant?.config?.bg ? `url(${tenant.config.bg})` : 'url(https://images.pexels.com/photos/326503/pexels-photo-326503.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2)',
            backgroundAttachment: 'fixed'
          }}
        >
          {shopTheme.overlay.enabled && (
            <div className="absolute inset-0 backdrop-blur-[2px]" style={{
              backgroundImage: shopTheme.overlay.useGradient
                ? `linear-gradient(${(
                    {
                      'to-b': 'to bottom',
                      'to-t': 'to top',
                      'to-r': 'to right',
                      'to-l': 'to left',
                      'to-tr': 'to top right',
                      'to-br': 'to bottom right',
                      'to-tl': 'to top left',
                      'to-bl': 'to bottom left',
                    } as Record<string, string>
                  )[shopTheme.overlay.orientation] || 'to bottom'},
                    ${hexToRgba(shopTheme.overlay.color1, shopTheme.overlay.opacity1)},
                    ${hexToRgba(shopTheme.overlay.color2, shopTheme.overlay.opacity2)},
                    ${hexToRgba(shopTheme.overlay.color3, shopTheme.overlay.opacity3)}
                  )`
                : undefined,
              backgroundColor: !shopTheme.overlay.useGradient
                ? hexToRgba(shopTheme.overlay.color1, shopTheme.overlay.opacity2)
                : undefined,
            }} />
          )}
        </div>

        {/* Particules flottantes avec couleurs et icônes personnalisables */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Top-Left */}
          {shopTheme.icons[0]?.enabled && (
            <motion.div
              className="absolute top-20 left-10"
              style={{ color: hexToRgba(shopTheme.icons[0].color, 0.2) }}
              animate={{ y: [-20, 20, -20], rotate: [0, 180, 360] }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            >
              {(() => {
                const IconComponent = iconMap[shopTheme.icons[0].icon] || iconMap.CreditCard;
                return IconComponent ? <IconComponent size={shopTheme.icons[0].size} /> : null;
              })()}
            </motion.div>
          )}
          {/* Top-Right */}
          {shopTheme.icons[1]?.enabled && (
            <motion.div
              className="absolute top-40 right-20"
              style={{ color: hexToRgba(shopTheme.icons[1].color, 0.2) }}
              animate={{ y: [20, -20, 20], rotate: [360, 180, 0] }}
              transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
            >
              {(() => {
                const IconComponent = iconMap[shopTheme.icons[1].icon] || iconMap.Sparkles;
                return IconComponent ? <IconComponent size={shopTheme.icons[1].size} /> : null;
              })()}
            </motion.div>
          )}
          {/* Bottom-Left */}
          {shopTheme.icons[2]?.enabled && (
            <motion.div
              className="absolute bottom-40 left-20"
              style={{ color: hexToRgba(shopTheme.icons[2].color, 0.2) }}
              animate={{ y: [-15, 15, -15], rotate: [0, 90, 180] }}
              transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
            >
              {(() => {
                const IconComponent = iconMap[shopTheme.icons[2].icon] || iconMap.Diamond;
                return IconComponent ? <IconComponent size={shopTheme.icons[2].size} /> : null;
              })()}
            </motion.div>
          )}
          {/* Bottom-Right */}
          {shopTheme.icons[3]?.enabled && (
            <motion.div
              className="absolute bottom-20 right-10"
              style={{ color: hexToRgba(shopTheme.icons[3].color, 0.2) }}
              animate={{ y: [15, -15, 15], rotate: [180, 270, 360] }}
              transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
            >
              {(() => {
                const IconComponent = iconMap[shopTheme.icons[3].icon] || iconMap.Star;
                return IconComponent ? <IconComponent size={shopTheme.icons[3].size} /> : null;
              })()}
            </motion.div>
          )}
        </div>

        <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-2 md:pt-32 md:pb-3">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <motion.div
              className="inline-flex items-center gap-2 backdrop-blur-sm rounded-full px-6 py-3 mb-8 mx-auto"
              style={{ 
                backgroundColor: `${tenant?.config?.color1 || '#fe8d17'}1a`,
                borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`,
                borderWidth: '1px'
              }}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              <CreditCard className="w-5 h-5" style={{ color: tenant?.config?.color1 || '#fe8d17' }} />
              <span className="font-medium" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Finalisation de Commande</span>
            </motion.div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-center mb-2 text-white">
              <span className="relative inline-block">
                <span 
                  className="absolute -inset-x-6 -inset-y-2 -z-10 rounded-full blur-lg"
                  style={{
                    backgroundImage: `linear-gradient(to right, ${hexToRgba(tenant?.config?.color2 || '#08062b', 0.3)}, ${hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.2)}, transparent)`
                  }}
                ></span>
                Paiement
              </span>
            </h1>
            <p className="text-center text-lg text-white/70 mb-8">Finalisez votre commande de produits en toute sécurité.</p>
          </motion.div>

          <form onSubmit={handleSubmit}>
            <motion.div 
              className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12"
              variants={fadeIn}
              initial="hidden"
              animate="visible"
            >
              {/* Colonne de gauche: Formulaires */}
              <div className="lg:col-span-2 space-y-8">
                <Card className="bg-white/10 backdrop-blur-sm shadow-xl text-white"
                  style={{
                    borderColor: `${tenant?.config?.color1 || '#fe8d17'}33`
                  }}
                >
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between" style={{ color: tenant?.config?.color3 || '#ffffff' }}>
                      <div className="flex items-center">
                        <CreditCard className="mr-2" style={{ color: tenant?.config?.color1 || '#fe8d17' }} size={24} />
                        Informations de livraison
                      </div>
                      {isAuthenticated && currentUserProfile && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleLogout}
                          className="text-xs px-2 py-1 h-auto"
                          style={{
                            color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.7),
                            borderColor: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.2)
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = tenant?.config?.color3 || '#ffffff';
                            e.currentTarget.style.backgroundColor = hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.1);
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = hexToRgba(tenant?.config?.color3 || '#ffffff', 0.7);
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <LogOut size={12} className="mr-1" />
                          Se déconnecter ou changer de compte
                        </Button>
                      )}
                    </CardTitle>
                    <CardDescription className="text-white/60">Où devons-nous envoyer votre commande ?</CardDescription>
                  </CardHeader>
                  <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-white/30" />
                      <Input name="fullName" value={formData.fullName} onChange={handleInputChange} placeholder="Nom complet" required className="pl-10 bg-white/10 border-white/20 placeholder:text-white/60"
                        style={{
                          color: tenant?.config?.color3 || '#ffffff',
                          '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
                        } as React.CSSProperties}
                      />
                    </div>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-white/30" />
                      <Input name="email" type="email" value={formData.email} onChange={handleInputChange} placeholder="Adresse e-mail" required className="pl-10 bg-white/10 border-white/20 placeholder:text-white/60"
                        style={{
                          color: tenant?.config?.color3 || '#ffffff',
                          '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
                        } as React.CSSProperties}
                      />
                    </div>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-white/30" />
                      <Input name="phone" type="tel" value={formData.phone} onChange={handleInputChange} placeholder="Téléphone" required className="pl-10 bg-white/10 border-white/20 placeholder:text-white/60"
                        style={{
                          color: tenant?.config?.color3 || '#ffffff',
                          '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
                        } as React.CSSProperties}
                      />
                    </div>
                    <div className="relative">
                      <Home className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-white/30" />
                      <Input name="address" value={formData.address} onChange={handleInputChange} placeholder="Adresse" required className="pl-10 bg-white/10 border-white/20 placeholder:text-white/60"
                        style={{
                          color: tenant?.config?.color3 || '#ffffff',
                          '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
                        } as React.CSSProperties}
                      />
                    </div>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-white/30" />
                      <Input name="city" value={formData.city} onChange={handleInputChange} placeholder="Ville" required className="pl-10 bg-white/10 border-white/20 placeholder:text-white/60"
                        style={{
                          color: tenant?.config?.color3 || '#ffffff',
                          '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
                        } as React.CSSProperties}
                      />
                    </div>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-white/30" />
                      <Input name="country" value={formData.country} onChange={handleInputChange} placeholder="Pays" required className="pl-10 bg-white/10 border-white/20 placeholder:text-white/60"
                        style={{
                          color: tenant?.config?.color3 || '#ffffff',
                          '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
                        } as React.CSSProperties}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Textarea name="notes" value={formData.notes} onChange={handleInputChange} placeholder="Ajouter une note à votre commande (facultatif)" className="bg-white/10 border-white/20 placeholder:text-white/60"
                        style={{
                          color: tenant?.config?.color3 || '#ffffff',
                          '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
                        } as React.CSSProperties}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Section de suggestion de connexion pour les non-connectés */}
                {!isAuthenticated && (
                  <Card className="bg-white/5 border backdrop-blur-sm shadow-xl text-white"
                    style={{
                      borderColor: `${tenant?.config?.color1 || '#fe8d17'}33`
                    }}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0">
                          <UserCheck size={24} style={{ color: tenant?.config?.color2 || '#008aef' }} />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold mb-2" style={{ color: tenant?.config?.color3 || '#ffffff' }}>
                            Connectez-vous pour une meilleure expérience
                          </h3>
                          <p className="text-sm mb-4" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.7) }}>
                            En vous connectant, vous bénéficiez de :
                          </p>
                          <ul className="text-sm space-y-2 mb-4" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8) }}>
                            <li className="flex items-center">
                              <CheckCircle size={14} className="mr-2" style={{ color: tenant?.config?.color2 || '#008aef' }} />
                              Pré-remplissage automatique de vos informations
                            </li>
                            <li className="flex items-center">
                              <CheckCircle size={14} className="mr-2" style={{ color: tenant?.config?.color2 || '#008aef' }} />
                              Historique de vos commandes
                            </li>
                            <li className="flex items-center">
                              <CheckCircle size={14} className="mr-2" style={{ color: tenant?.config?.color2 || '#008aef' }} />
                              Suivi de livraison en temps réel
                            </li>
                            <li className="flex items-center">
                              <CheckCircle size={14} className="mr-2" style={{ color: tenant?.config?.color2 || '#008aef' }} />
                              Support client prioritaire
                            </li>
                          </ul>
                          <div className="pt-2">
                            <Button 
                              onClick={() => navigate('/boutique/commandes?redirect=/boutique/checkout')}
                              size="sm"
                              className="shadow-lg hover:shadow-xl transition-all duration-300"
                              style={{
                                backgroundColor: tenant?.config?.color2 || '#008aef',
                                color: tenant?.config?.color3 || '#ffffff',
                                backgroundImage: `linear-gradient(to right, ${tenant?.config?.color2 || '#008aef'}, ${hexToRgba(tenant?.config?.color2 || '#008aef', 0.8)})`
                              }}
                            >
                              <UserPlus size={16} className="mr-2" />
                              Se connecter
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* La section de méthode de paiement a été supprimée car elle est maintenant gérée par le modal de paiement */}
              </div>

              {/* Colonne de droite: Résumé de la commande */}
              <div className="lg:col-span-1">
                <div className="sticky top-24">
                  <Card className="bg-white/10 backdrop-blur-sm shadow-xl text-white"
                    style={{
                      borderColor: `${tenant?.config?.color1 || '#fe8d17'}33`
                    }}
                  >
                    <CardHeader>
                      <CardTitle>Résumé de la commande</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="divide-y divide-white/10">
                        {cartItems.map((item) => (
                          <div key={item.productId} className="flex items-center justify-between py-3">
                            <div className="flex items-center space-x-4">
                              <div className="relative">
                                <img src={item.imageUrl || 'https://via.placeholder.com/64'} alt={item.title} className="w-16 h-16 rounded-md object-cover border border-white/10" />
                                <span className="absolute -top-2 -right-2 text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center"
                                  style={{
                                    backgroundColor: tenant?.config?.color1 || '#fe8d17',
                                    color: tenant?.config?.color3 || '#ffffff'
                                  }}
                                >{item.quantity}</span>
                              </div>
                              <div>
                                <p className="font-semibold">{item.title}</p>
                                <p className="text-sm text-white/60">{new Intl.NumberFormat('fr-FR').format(item.price) + ' FCFA'}</p>
                              </div>
                            </div>
                            <p className="font-semibold">{new Intl.NumberFormat('fr-FR').format(item.price * item.quantity) + ' FCFA'}</p>
                          </div>
                        ))}
                      </div>
                      <div className="border-t border-white/10 pt-4 space-y-2">
                        <div className="flex justify-between text-white/80">
                          <span>Sous-total</span>
                          <span>{new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(cartTotal)}</span>
                        </div>
                        <div className="flex justify-between text-white/80">
                          <span>Livraison</span>
                          <span>À calculer</span>
                        </div>
                        <div className="border-t border-white/10 pt-4 flex justify-between font-bold text-lg">
                          <span>Total</span>
                          <span>{new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(cartTotal)}</span>
                        </div>
                      </div>
                    </CardContent>
                    <CardFooter className="flex-col items-stretch space-y-4">
                      <Button type="submit" disabled={isSubmitting} size="lg" className="w-full shadow-lg hover:shadow-xl transition-all duration-300 font-bold"
                        style={{
                          backgroundColor: tenant?.config?.color1 || '#08062b',
                          color: tenant?.config?.color3 || '#ffffff',
                          borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`,
                          boxShadow: `0 4px 15px ${hexToRgba(tenant?.config?.color1 || '#08062b', 0.4)}`
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = `${tenant?.config?.color2 || '#08062b'}e6`;
                          e.currentTarget.style.boxShadow = `0 6px 20px ${hexToRgba(tenant?.config?.color2 || '#08062b', 0.6)}`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = tenant?.config?.color1 || '#08062b';
                          e.currentTarget.style.boxShadow = `0 4px 15px ${hexToRgba(tenant?.config?.color1 || '#08062b', 0.4)}`;
                        }}
                      >
                        {isSubmitting ? <Loader2 className="animate-spin mr-2" style={{ color: tenant?.config?.color1 || '#fe8d17' }} /> : <Lock className="mr-2" />}
                        {isSubmitting ? 'Traitement...' : `Payer ${new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(cartTotal)}`}
                      </Button>
                      <p className="text-xs text-center text-white/50">Paiement sécurisé. Les informations sont cryptées.</p>
                    </CardFooter>
                  </Card>
                </div>
              </div>
            </motion.div>
          </form>
        </div>
      </div>

      {/* Modal de paiement */}
      {showPaymentModal && currentOrder && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          order={currentOrder}
          onPaymentSuccess={() => {
            // Vider le panier après paiement réussi
            localStorage.removeItem('shop_cart');
            
            // Rediriger vers la page de confirmation après le paiement réussi
            navigate(`/boutique/confirmation?order=${currentOrder.order_code}`);
            toast({
              title: 'Paiement confirmé',
              description: 'Votre commande a été traitée avec succès',
              variant: 'default'
            });
          }}
        />
      )}
    </PublicLayout>
  );
};

export default CheckoutPage;
